/**
 * What the tenant Front Desk (group `business_invitation_all`, no `custom:business_id`) can do in each section of
 * the admin: Documentation/specs/front-desk-tenant-permisos.md (Rodrigo, 9 Oct 2026). It sees everything but
 * Advanced, Business > Transactions and Gatekeepers; the sections differ in their actions. Never deletes.
 *
 * The grant is this static table, indexed by the Cognito group of the ID token at login (`business_invitation_all`
 * and no admin or tenant group, see useUserPermissions): roles are created in Cognito and the admin asks no endpoint
 * for them (Rodrigo, 15 Oct 2026). It only decides what the UI SHOWS. The server re-checks every write (403 outside
 * this table). It is the single place that lists the Front Desk's grant, so the menu, the routes and the buttons
 * cannot drift apart.
 */
import type { UserPermissions } from '../hooks/useUserPermissions';

export type FdSection =
  | 'dashboard' | 'passes' | 'bookings' | 'business' | 'transactions' | 'estates' | 'users'
  | 'events' | 'cms' | 'notifications' | 'employees' | 'advanced' | 'team';

export type FdAction = 'view' | 'create' | 'edit' | 'cancel' | 'approve' | 'audit';

const FD_MATRIX: Record<FdSection, readonly FdAction[]> = {
  dashboard: ['view'],
  passes: ['view', 'create', 'edit', 'cancel'],
  // Client Booking on behalf of a client (SAM-801); cancelling refunds or voids on the server when the rules apply.
  bookings: ['view', 'create', 'cancel'],
  // Business list and file: Activity, Services, Products, Team and Settings, all read-only.
  business: ['view'],
  transactions: [],
  estates: ['view'],
  // Create residents, edit basic data (name, phone, photo) and approve owner / resident / property manager requests.
  // `audit`: read the change history of a user (who changed what and when).
  users: ['view', 'create', 'edit', 'approve', 'audit'],
  events: ['view'],
  cms: ['view'],
  notifications: ['view', 'create'],
  // Gatekeepers (the gates' staff) are for the tenant admin and Xhinola only (Rodrigo, 15 Oct 2026): not the Front Desk.
  employees: [],
  advanced: [],
  // Business > (business) > Team (SAM-1297): the tenant Front Desk adds employees of its tenant's businesses; they are reviewed by Security.
  team: ['view', 'create'],
};

export const fdCan = (section: FdSection, action: FdAction): boolean => FD_MATRIX[section].includes(action);

/**
 * SAM-1108 (M7): the Estate Group Admin and the Estate Group Front Desk (Cognito groups `estate_group_admin` and
 * `estate_group_front_desk`, Documentation/specs/estate-group-roles.md §4). They work through the same screens as
 * the tenant Front Desk, over the estates and businesses of their own estate groups (the server filters every
 * read and write by `custom:estate_group_ids`, fail closed), never edit users and never delete. Where the spec
 * left a choice, the more restrictive one:
 * - Notifications: hidden until the send by estate exists (SAM-1114); crud answers 403 to their reads today.
 * - Settings > Employees (creating Front Desks of their groups) and Project Gates (§7): no screen yet, hidden.
 * - Users: read only, no change history (`audit`), no approving requests (decision 3, 15 Oct).
 */
const EG_FRONT_DESK_MATRIX: Record<FdSection, readonly FdAction[]> = {
  dashboard: ['view'],
  passes: ['view', 'create', 'edit', 'cancel'],
  bookings: ['view', 'create', 'cancel'],
  business: ['view'],
  transactions: [],
  estates: ['view'],
  users: ['view'],
  events: ['view'],
  cms: ['view'],
  notifications: [],
  employees: [],
  advanced: [],
  team: [],
};

const EG_ADMIN_MATRIX: Record<FdSection, readonly FdAction[]> = {
  ...EG_FRONT_DESK_MATRIX,
  // Edits its estates; never their estate group, and buildings stay read-only (Rodrigo, 23 Oct).
  estates: ['view', 'edit'],
};

/**
 * SAM-1321: the estate group admin that is the Owner / Admin of the business administering the group. It works in
 * its own business, so it also sees Business > Team to add its Front Desks (Operators); the M7 admin has no business.
 */
const EG_ADMIN_BUSINESS_MATRIX: Record<FdSection, readonly FdAction[]> = {
  ...EG_ADMIN_MATRIX,
  team: ['view', 'create'],
};

/** The roles whose screens follow a static matrix instead of the older per-group flags. */
export type MatrixRole = 'tenant_front_desk' | 'estate_group_admin' | 'estate_group_admin_business' | 'estate_group_front_desk';

const MATRICES: Record<MatrixRole, Record<FdSection, readonly FdAction[]>> = {
  tenant_front_desk: FD_MATRIX,
  estate_group_admin: EG_ADMIN_MATRIX,
  estate_group_admin_business: EG_ADMIN_BUSINESS_MATRIX,
  estate_group_front_desk: EG_FRONT_DESK_MATRIX,
};

type MatrixFlags = Pick<UserPermissions, 'isTenantFrontDesk'> &
  Partial<Pick<UserPermissions, 'isEstateGroupAdmin' | 'isEstateGroupFrontDesk' | 'estateGroupViaBusiness'>>;

/** Which matrix governs this session, or null when the page's own role checks do (useUserPermissions sets at most one). */
export const matrixRoleOf = (p: MatrixFlags): MatrixRole | null => {
  if (p.isTenantFrontDesk) return 'tenant_front_desk';
  if (p.isEstateGroupFrontDesk) return 'estate_group_front_desk';
  if (p.isEstateGroupAdmin) return p.estateGroupViaBusiness ? 'estate_group_admin_business' : 'estate_group_admin';
  return null;
};

export const roleCan = (role: MatrixRole, section: FdSection, action: FdAction): boolean =>
  MATRICES[role][section].includes(action);

/**
 * The tenant Front Desk or an estate group role: staff with no business of its own, for whom the endpoints that
 * answer 403 to the tenant Front Desk (access_settings list, booking receipt and changelog, cancel requests) answer
 * 403 too, and whose passes take their business from the destination picked.
 */
export const worksAsFrontDesk = (p: MatrixFlags): boolean => matrixRoleOf(p) !== null;

/**
 * `allowed` is the answer for everyone else (the page's own role check); the tenant Front Desk and the estate
 * group roles get their matrix instead, whatever the other flags say.
 */
export const canAct = (
  permissions: MatrixFlags,
  section: FdSection,
  action: FdAction,
  allowed: boolean,
): boolean => {
  const role = matrixRoleOf(permissions);
  return role ? roleCan(role, section, action) : allowed;
};
